import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Trophy, Medal, Award, Crown, Zap } from "lucide-react";

import { BACKEND_URL } from "@/lib/api";
import { LEADERBOARD_POLL_INTERVAL_MS, EXACT_REWARD_POOL, CLOSE_REWARD_POOL } from "@/constants";

const Leaderboard = ({ user }) => {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const [leaderboard, setLeaderboard] = useState([]);
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();

    const interval = setInterval(() => {
      fetchData();
    }, LEADERBOARD_POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [matchId]);

  const fetchData = async () => {
    try {
      const [leaderboardRes, matchRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/leaderboard/${matchId}`),
        fetch(`${BACKEND_URL}/api/matches/${matchId}`),
      ]);

      const leaderboardData = await leaderboardRes.json();
      const matchData = await matchRes.json();

      setLeaderboard(leaderboardData);
      setMatch(matchData);
      setLoading(false);
    } catch (_error) {
      setLoading(false);
    }
  };

  const getPositionIcon = (index) => {
    if (index === 0) return <Crown className="w-6 h-6 text-yellow-500" />;
    if (index === 1) return <Medal className="w-6 h-6 text-slate-400" />;
    if (index === 2) return <Award className="w-6 h-6 text-amber-700" />;
    return (
      <span className="w-6 h-6 flex items-center justify-center font-black text-slate-600 text-sm">
        {index + 1}
      </span>
    );
  };

  const getRewardBadge = (rewardStatus) => {
    if (rewardStatus === "exact") {
      return (
        <Badge className="bg-gradient-to-r from-amber-500 to-yellow-400 text-white font-black uppercase text-xs">
          🎯 EXACT!
        </Badge>
      );
    } else if (rewardStatus === "close") {
      return (
        <Badge className="bg-gradient-to-r from-[#02B8A5] to-teal-500 text-white font-black uppercase text-xs">
          🔥 CLOSE!
        </Badge>
      );
    }
    return null;
  };

  const calculateRewardShare = () => {
    if (!match || match.status !== "completed") return null;

    const exactMatches = leaderboard.filter((entry) => entry.reward_status === "exact").length;
    const closeMatches = leaderboard.filter((entry) => entry.reward_status === "close").length;

    return {
      exactCount: exactMatches,
      exactReward: exactMatches > 0 ? Math.floor(EXACT_REWARD_POOL / exactMatches) : 0,
      closeCount: closeMatches,
      closeReward: closeMatches > 0 ? Math.floor(CLOSE_REWARD_POOL / closeMatches) : 0,
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#02B8A5] mx-auto"></div>
          <p className="mt-4 text-sm font-bold uppercase tracking-wider text-slate-600">
            Loading leaderboard...
          </p>
        </div>
      </div>
    );
  }

  const rewardShare = calculateRewardShare();

  return (
    <div className="min-h-screen bg-slate-50 pb-8">
      {/* Header */}
      <div className="stadium-night text-white py-6 px-4 mb-6">
        <div className="max-w-md mx-auto">
          <Button
            variant="ghost"
            className="text-white hover:bg-white/10 mb-4 -ml-2 font-bold uppercase tracking-wide"
            onClick={() => navigate(`/match/${matchId}`)}
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            BACK
          </Button>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-3"
          >
            <Trophy className="w-10 h-10" />
            <div>
              <h1
                className="text-3xl font-black uppercase tracking-tighter"
                data-testid="leaderboard-title"
              >
                LEADERBOARD
              </h1>
              {match && (
                <p className="text-white/90 text-sm font-medium uppercase tracking-wide">
                  MATCH {match.match_number} - {match.venue}
                </p>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 space-y-5">
        {/* Match Stats */}
        {match && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="bg-gradient-to-r from-slate-50 via-white to-slate-50 border-2 border-slate-200 rounded-xl shadow-sm">
              <CardContent className="py-6">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                      PREDICTIONS
                    </p>
                    <p
                      className="text-3xl font-black text-[#02B8A5] score-number"
                      data-testid="total-predictions"
                    >
                      {leaderboard.length}
                    </p>
                  </div>
                  {match.status === "completed" && match.total_runs && (
                    <>
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                          TOTAL RUNS
                        </p>
                        <p
                          className="text-3xl font-black text-slate-900 score-number"
                          data-testid="actual-runs"
                        >
                          {match.total_runs}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                          WINNERS
                        </p>
                        <p
                          className="text-3xl font-black text-green-600 score-number"
                          data-testid="winners-count"
                        >
                          {(rewardShare?.exactCount || 0) + (rewardShare?.closeCount || 0)}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Reward Distribution section removed as per user request */}

        {/* Leaderboard */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="rounded-xl shadow-lg border-2 border-slate-200">
            <CardHeader>
              <CardTitle className="font-black uppercase tracking-wider">PREDICTIONS</CardTitle>
            </CardHeader>
            <CardContent>
              {leaderboard.length === 0 ? (
                <div className="text-center py-12">
                  <Trophy className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600 font-semibold">No predictions yet</p>
                  <p className="text-sm text-slate-500 mt-2">Be the first to predict!</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {leaderboard.map((entry, index) => (
                    <motion.div
                      key={`${entry.username}-${index}`}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3 + index * 0.05 }}
                      className={`flex items-center gap-3 p-4 rounded-xl transition-all ${
                        entry.username === user.username
                          ? "bg-gradient-to-r from-[#02B8A5]/10 to-teal-50 border-2 border-[#02B8A5] shadow-md"
                          : index < 3
                            ? "bg-gradient-to-r from-slate-100 to-slate-50 border border-slate-300"
                            : "bg-white border border-slate-200 hover:bg-slate-50"
                      }`}
                      data-testid={`leaderboard-entry-${index}`}
                    >
                      <div className="flex-shrink-0">{getPositionIcon(index)}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-black text-base text-slate-900 uppercase tracking-wide truncate">
                            {entry.username}
                          </p>
                          {entry.username === user.username && (
                            <Badge variant="outline" className="text-xs font-bold">
                              YOU
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs font-semibold">
                          <span className="text-slate-600">
                            Predicted:{" "}
                            <span className="font-black text-[#02B8A5]">
                              {entry.predicted_runs}
                            </span>
                          </span>
                          {entry.difference !== null && (
                            <span className="text-slate-600">
                              Diff: <span className="font-black">{entry.difference}</span>
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        {entry.reward_status && getRewardBadge(entry.reward_status)}
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-3 pt-4">
          <Button
            variant="outline"
            className="border-2 border-slate-300 font-bold uppercase tracking-wider hover:bg-slate-100 py-6"
            onClick={() => navigate(`/match/${matchId}`)}
            data-testid="view-match-button"
          >
            VIEW MATCH
          </Button>
          <Button
            className="primary-action text-white font-bold uppercase tracking-wider shadow-lg py-6"
            onClick={() => navigate("/")}
            data-testid="all-matches-button"
          >
            ALL MATCHES
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;
